import { WidgetFormValues } from '@/lib/widget-schema'

export interface WidgetTheme {
	id: string
	name: string
	description?: string
	config: Partial<WidgetFormValues>
}

export const widgetThemes: WidgetTheme[] = [
	{
		id: 'bottom-dock-electric',
		name: 'Bottom Dock Electric',
		description: 'Mobile app dock navigation with bottom module tabs',
		config: {
			showHeader: true,
			header_title: 'How can we help you today?',
			position: 'bottom-center',
			primaryColor: '#5B5CF0',
			gradientEndColor: '#4F46E5',
			backgroundColor: '#F8FAFF',
			textColor: '#111827',
			animation: { header: 'subtle-gradient-shift', backgroundEffects: true },
			glassmorphism: {
				enabled: true,
				backdropBlur: '12px',
				borderColor: 'rgba(79, 70, 229, 0.2)',
			},
			moduleNavigation: {
				enabled: true,
				mode: 'bottom-tabs',
				defaultToFirstModule: true,
				modules: ['feedback', 'mailbox', 'bug-report', 'feature-request', 'roadmap'],
			},
			layout: {
				spacing: 'default',
				menuPadding: 'px-4 py-3',
				itemSpacing: 'space-y-2',
				containerBorder: true,
				headerPadding: 'p-5 pt-12',
				headerBackground: 'rgba(91, 92, 240, 1)',
				headerTextColor: '#FFFFFF',
			},
			menuItemStyle: {
				layout: 'separate',
				background: '#FFFFFF',
				textColor: '#111827',
				showDescriptions: false,
				useBorder: true,
				borderColor: '#E0E7FF',
				centerAlign: true,
				verticalAlign: false,
			},
			menuItemColors: {
				feedback_bg: '#EEF2FF',
				feedback_icon: '#5B5CF0',
				review_bg: '#F5F3FF',
				review_icon: '#7C3AED',
				bug_bg: '#FFE4E6',
				bug_icon: '#E11D48',
				contact_bg: '#E0F2FE',
				contact_icon: '#0284C7',
				feature_bg: '#CCFBF1',
				feature_icon: '#0F766E',
			},
			brandingStyle: {
				borderColor: '#C7D2FE',
			},
		},
	},
	
	{
		id: 'minimal',
		name: 'Minimal Light',
		description: 'Clean light theme with bordered cards and colored icons',
		config: {
			showHeader: true,
			header_title: '',
			primaryColor: '#6B7280',
			gradientEndColor: null,
			backgroundColor: '#FFFFFF',
			textColor: '#111827',
			animation: { header: 'none' },
			brandingStyle: {
				borderColor: '#E5E7EB',
			},
			glassmorphism: {
				enabled: false,
				backdropBlur: '0px',
				borderColor: 'rgba(229, 231, 235, 1)',
			},
			layout: {
				spacing: 'default',
				menuPadding: 'px-4 py-4',
				itemSpacing: 'space-y-2',
				containerBorder: true,
				headerPadding: 'px-4 py-2 pt-8',
				headerBackground: '#FFFFFF',
				headerTextColor: '#111827',
			},
			menuItemStyle: {
				layout: 'separate',
				background: '#FFFFFF',
				textColor: '#111827',
				showDescriptions: false,
				useBorder: true,
				borderColor: '#E5E7EB',
				centerAlign: false,
				verticalAlign: true,
			},
			menuItemColors: {
				feedback_bg: '#FEF3C7',
				feedback_icon: '#D97706',
				review_bg: '#DBEAFE',
				review_icon: '#2563EB',
				bug_bg: '#FEE2E2',
				bug_icon: '#DC2626',
				feature_bg: '#DCFCE7',
				feature_icon: '#16A34A',
			},
		},
	},
	
	
	{
		id: 'editorial-contrast',
		name: 'Editorial Contrast',
		description: 'Editorial contrast theme with clean structured cards',
		config: {
			showHeader: true,
			header_title: 'How can we help you today?',
			position: 'bottom-right',
			primaryColor: '#111827',
			gradientEndColor: '#2563EB',
			backgroundColor: '#F9FAFB',
			textColor: '#0F172A',
			animation: { header: 'subtle-gradient-shift', backgroundEffects: false },
			brandingStyle: {
				borderColor: '#CBD5E1',
			},
			glassmorphism: {
				enabled: false,
			},
			layout: {
				spacing: 'spacious',
				menuPadding: 'px-6 pt-4 pb-4',
				itemSpacing: 'space-y-3',
				containerBorder: true,
				headerPadding: 'p-6 pt-12',
				headerBackground: '#0F172A',
				headerTextColor: '#F8FAFC',
			},
			menuItemStyle: {
				layout: 'separate',
				background: '#FFFFFF',
				textColor: '#0F172A',
				showDescriptions: true,
				useBorder: true,
				borderColor: '#E2E8F0',
				centerAlign: false,
				verticalAlign: true,
			},
			menuItemColors: {
				feedback_bg: '#EFF6FF',
				feedback_icon: '#1D4ED8',
				review_bg: '#F5F3FF',
				review_icon: '#7C3AED',
				bug_bg: '#FFF1F2',
				bug_icon: '#BE123C',
				contact_bg: '#F0FDFA',
				contact_icon: '#0F766E',
				feature_bg: '#F1F5F9',
				feature_icon: '#0F172A',
			},
		},
	},
	{
		id: 'quadrille-grid',
		name: 'Quadrille Grid',
		description: 'Two-column grid layout for module buttons',
		config: {
			showHeader: true,
			header_title: 'How can we help you today?',
			position: 'middle-right',
			primaryColor: '#3B82F6',
			gradientEndColor: '#6366F1',
			backgroundColor: '#F8FAFC',
			textColor: '#0F172A',
			animation: { header: 'subtle-gradient-shift', backgroundEffects: false },
			brandingStyle: {
				borderColor: '#BFDBFE',
			},
			glassmorphism: {
				enabled: false,
			},
			layout: {
				spacing: 'default',
				menuPadding: 'px-4 py-4',
				itemSpacing: 'space-y-3',
				menuColumns: 2,
				containerBorder: true,
				headerPadding: 'p-5 pt-12',
				headerBackground: '#3B82F6',
				headerTextColor: '#FFFFFF',
			},
			menuItemStyle: {
				layout: 'separate',
				background: '#FFFFFF',
				textColor: '#0F172A',
				showDescriptions: false,
				centerAlign: true,
				verticalAlign: false,
				useBorder: true,
				borderColor: '#E2E8F0',
			},
			menuItemColors: {
				feedback_bg: '#DBEAFE',
				feedback_icon: '#2563EB',
				review_bg: '#F3E8FF',
				review_icon: '#7C3AED',
				bug_bg: '#FEE2E2',
				bug_icon: '#DC2626',
				contact_bg: '#CFFAFE',
				contact_icon: '#0891B2',
				feature_bg: '#DCFCE7',
				feature_icon: '#16A34A',
			},
			moduleNavigation: {
				enabled: false,
			},
		},
	},
	{
		id: 'dark-minimal',
		name: 'Minimal Dark',
		description: 'Clean dark theme with bordered cards and colored icons',
		config: {
			showHeader: true,
			header_title: '',
			primaryColor: '#9CA3AF',
			gradientEndColor: null,
			backgroundColor: '#111827',
			textColor: '#F3F4F6',
			animation: { header: 'none' },
			brandingStyle: {
				borderColor: '#374151',
			},
			glassmorphism: {
				enabled: false,
				backdropBlur: '0px',
				borderColor: '#111827',
			},
			layout: {
				spacing: 'default',
				menuPadding: 'px-4 py-4',
				itemSpacing: 'space-y-3',
				containerBorder: true,
				headerPadding: 'px-4 py-2 pt-8',
				headerBackground: '#111827',
				headerTextColor: '#F3F4F6',
			},
			menuItemStyle: {
				layout: 'separate',
				background: '#111827',
				textColor: '#F3F4F6',
				showDescriptions: false,
				centerAlign: false,
				verticalAlign: true,
				useBorder: true,
				borderColor: '#374151',
			},
			menuItemColors: {
				feedback_bg: 'rgba(217, 119, 6, 0.2)',
				feedback_icon: '#FBBF24',
				review_bg: 'rgba(59, 130, 246, 0.2)',
				review_icon: '#60A5FA',
				bug_bg: 'rgba(239, 68, 68, 0.2)',
				bug_icon: '#F87171',
				contact_bg: 'rgba(14, 165, 233, 0.2)',
				contact_icon: '#38BDF8',
				feature_bg: 'rgba(139, 92, 246, 0.2)',
				feature_icon: '#A78BFA',
			},
		},
	},
	{
		id: 'midnight',
		name: 'Midnight',
		description: 'Sleek dark theme with zinc palette and purple gradients',
		config: {
			showHeader: true,
			header_title: 'How can we help you today?',
			primaryColor: '#9333EA',
			gradientEndColor: '#7C3AED',
			backgroundColor: 'rgba(24, 24, 27, 0.95)',
			textColor: '#F4F4F5',
			animation: { header: 'none' },
			glassmorphism: {
				enabled: true,
				backdropBlur: '12px',
				borderColor: 'rgba(63, 63, 70, 0.5)',
			},
			layout: {
				spacing: 'default',
				menuPadding: 'px-4 py-4',
				itemSpacing: 'space-y-3',
				containerBorder: false,
			},
			menuItemStyle: {
				layout: 'default',
				background: 'rgba(39, 39, 42, 0.5)',
				textColor: '#F4F4F5',
			},
			menuItemColors: {
				feedback_bg: 'rgba(245, 158, 11, 0.1)',
				feedback_icon: '#FCD34D',
				review_bg: 'rgba(168, 85, 247, 0.1)',
				review_icon: '#C084FC',
				bug_bg: 'rgba(239, 68, 68, 0.1)',
				bug_icon: '#FCA5A5',
				contact_bg: 'rgba(14, 165, 233, 0.18)',
				contact_icon: '#38BDF8',
				feature_bg: 'rgba(52, 211, 153, 0.1)',
				feature_icon: '#86EFAC',
			},
		},
	},
]
